// Mobile ticket detail: status progression, time tracking, notes, parts,
// visit outcomes. Uses only the existing dispatch API endpoints.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent,
  Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Add as AddIcon,
  CheckCircle as CompleteIcon,
  Delete as DeleteIcon,
  Directions as DirectionsIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as OutcomeIcon,
  LocalShipping as TruckIcon,
  NavigationOutlined as EnRouteIcon,
  Phone as PhoneIcon,
  PlayArrow as StartIcon,
  Remove as RemoveIcon,
  Replay as ReturnIcon,
  ShoppingCart as PartsIcon,
  Stop as StopIcon,
  Undo as BackToDispatchIcon,
  Room as OnSiteIcon,
} from '@mui/icons-material';
import {
  inventoryApi, lineItemsApi, partsApi, ticketsApi, timeTrackingApi,
} from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  CONCEPT_GRADIENT, ReturnTripChip, StatusChip, TransferRequestDialog,
  fmtMinutes, fmtSched, mapsHref, ticketAddress, toLocalIso,
} from './TechShared';
import { TechPicturesCard } from './TechPicturesCard';   // Files tab: take a picture -> ticket (Tech submitted) 2026-09-04

const STEPS: Array<{ status: string; label: string; icon: JSX.Element }> = [
  { status: 'en_route', label: 'En Route', icon: <EnRouteIcon /> },
  { status: 'on_site', label: 'On Site', icon: <OnSiteIcon /> },
  { status: 'in_progress', label: 'Working', icon: <StartIcon /> },
];
const STEP_ORDER = ['assigned', 'scheduled', 'en_route', 'on_site', 'in_progress'];

export default function TechTicketPage() {
  const { id } = useParams();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [ticket, setTicket] = useState<any | null>(null);
  const [timeSummary, setTimeSummary] = useState<any | null>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // notes
  const [notes, setNotes] = useState({ internal_notes: '', customer_notes: '', resolution_notes: '' });
  const [notesDirty, setNotesDirty] = useState(false);

  // parts
  const [partsQuery, setPartsQuery] = useState('');
  const [partsResults, setPartsResults] = useState<any[]>([]);
  const [addPart, setAddPart] = useState<any | null>(null); // part selected for the add dialog
  const [addForm, setAddForm] = useState({ quantity: 1, item_condition: 'new', discount_percent: 0 });
  const [addStock, setAddStock] = useState<any[]>([]);
  const [myTruck, setMyTruck] = useState<string | undefined>(undefined);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferPart, setTransferPart] = useState<any | null>(null);

  // manual time entry
  const [manualEntry, setManualEntry] = useState({ entry_type: 'onsite', started_at: '', ended_at: '', notes: '' });

  // outcome dialog: 'complete' | 'return_trip' | 'return_to_dispatch'
  const [outcome, setOutcome] = useState<string | null>(null);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  // change 16 (2026-11-01, P2-E): the API's plain-language refusal (403) when Complete needs dispatcher approval
  const [closeRefused, setCloseRefused] = useState<string | null>(null);

  // live timer tick
  const [, setTick] = useState(0);
  const tickRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [t, ts, li] = await Promise.all([
        ticketsApi.get(ticketId),
        timeTrackingApi.getTicketSummary(ticketId).catch(() => ({ data: null })),
        lineItemsApi.list(ticketId).catch(() => ({ data: [] })),
      ]);
      setTicket(t.data);
      setTimeSummary(ts.data);
      setLineItems(li.data || []);
      setNotes({
        internal_notes: t.data.internal_notes || '',
        customer_notes: t.data.customer_notes || '',
        resolution_notes: t.data.resolution_notes || '',
      });
      setNotesDirty(false);
    } catch {
      setError('Could not load this ticket.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('tech-refresh', h);
    return () => window.removeEventListener('tech-refresh', h);
  }, [load]);
  useEffect(() => {
    inventoryApi.warehouses()
      .then((r) => setMyTruck((r.data || []).find((w: any) => w.is_mine)?.warehouse))
      .catch(() => {});
  }, []);

  const openEntry = (timeSummary?.entries || []).find((e: any) => !e.ended_at);
  useEffect(() => {
    if (openEntry && tickRef.current === null) {
      tickRef.current = window.setInterval(() => setTick((n) => n + 1), 1000);
    }
    if (!openEntry && tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    return () => {
      if (tickRef.current !== null) { window.clearInterval(tickRef.current); tickRef.current = null; }
    };
  }, [openEntry?.id]);

  const refreshTime = async () => {
    try { const ts = await timeTrackingApi.getTicketSummary(ticketId); setTimeSummary(ts.data); } catch {}
  };

  // ── status progression ────────────────────────────────────────────────
  const setStatus = async (status: string) => {
    setBusy(status);
    try {
      await ticketsApi.changeStatus(ticketId, status);
      enqueueSnackbar(`Status → ${status.replace(/_/g, ' ').toUpperCase()}`, { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || 'Status change failed', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  // ── timer ─────────────────────────────────────────────────────────────
  const startTimer = async (entryType: 'onsite' | 'travel') => {
    if (!user?.id) return;
    setBusy(`timer-${entryType}`);
    try {
      await timeTrackingApi.createEntry({
        ticket_id: ticketId,
        tech_id: ticket?.assigned_to_id || user.id,
        entry_type: entryType,
        started_at: toLocalIso(new Date()),
        billable: true,
      });
      await refreshTime();
      enqueueSnackbar(`${entryType === 'onsite' ? 'On-site' : 'Travel'} timer started`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not start timer', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const stopTimer = async (entry: any) => {
    setBusy('timer-stop');
    try {
      await timeTrackingApi.updateEntry(entry.id, { ended_at: toLocalIso(new Date()), reason: 'tech app timer stop' });
      await refreshTime();
      enqueueSnackbar('Timer stopped', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not stop timer', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const addManualEntry = async () => {
    if (!manualEntry.started_at || !manualEntry.ended_at || !user?.id) return;
    setBusy('manual');
    try {
      await timeTrackingApi.createEntry({
        ticket_id: ticketId,
        tech_id: ticket?.assigned_to_id || user.id,
        entry_type: manualEntry.entry_type,
        started_at: manualEntry.started_at,
        ended_at: manualEntry.ended_at,
        notes: manualEntry.notes,
        billable: true,
      });
      setManualEntry({ entry_type: 'onsite', started_at: '', ended_at: '', notes: '' });
      await refreshTime();
      enqueueSnackbar('Time entry added (flagged ADJUSTED for billing review)', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not add entry', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const adjustEntry = async (e: any) => {
    const v = window.prompt('New duration in minutes (adjustments are flagged for billing review):', String(Math.round(e.duration_minutes)));
    if (v === null) return;
    const mins = parseFloat(v);
    if (isNaN(mins) || mins < 0) return;
    const reason = window.prompt('Reason for adjustment (optional):') || undefined;
    try {
      await timeTrackingApi.updateEntry(e.id, { duration_minutes: mins, reason });
      await refreshTime();
    } catch {
      enqueueSnackbar('Could not adjust entry', { variant: 'error' });
    }
  };

  const deleteEntry = async (e: any) => {
    if (!window.confirm('Delete this time entry?')) return;
    try { await timeTrackingApi.deleteEntry(e.id); await refreshTime(); } catch {}
  };

  const liveMinutes = openEntry
    ? Math.max(0, (Date.now() - new Date(openEntry.started_at).getTime()) / 60000)
    : 0;

  // ── notes ─────────────────────────────────────────────────────────────
  const saveNotes = async () => {
    setBusy('notes');
    try {
      await ticketsApi.update(ticketId, { ...notes, version: ticket?.version });
      enqueueSnackbar('Notes saved', { variant: 'success' });
      await load();
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.status === 409
          ? 'Ticket changed elsewhere — refresh and retry'
          : (e?.response?.data?.detail || 'Could not save notes'),
        { variant: 'error' },
      );
    } finally {
      setBusy(null);
    }
  };

  // ── parts ─────────────────────────────────────────────────────────────
  const searchParts = async (q: string) => {
    setPartsQuery(q);
    if (q.trim().length < 2) { setPartsResults([]); return; }
    try { const r = await partsApi.search(q); setPartsResults(r.data || []); } catch { setPartsResults([]); }
  };

  const pickPart = (p: any) => {
    setAddPart(p);
    setAddForm({ quantity: 1, item_condition: 'new', discount_percent: 0 });
    setAddStock([]);
    inventoryApi.partStock(p.part_number).then((r) => setAddStock(r.data || [])).catch(() => setAddStock([]));
  };

  const truckQty = (() => {
    if (!myTruck) return null;
    const row = addStock.find((s: any) => s.warehouse === myTruck);
    return row ? row.qty : 0;
  })();

  const submitAddPart = async () => {
    if (!addPart) return;
    setBusy('addpart');
    try {
      const r = await lineItemsApi.add(ticketId, {
        part_id: addPart.id,
        part_number: addPart.part_number,
        description: addPart.name,
        unit_price: addPart.unit_price,
        unit_cost: addPart.unit_cost,
        unit_of_measure: addPart.unit_of_measure,
        quantity: addForm.quantity,
        category: addPart.category || 'part',
        taxable: true,
        item_condition: addForm.item_condition,
        discount_percent: addForm.discount_percent || 0,
      });
      setLineItems((prev) => [...prev, r.data]);
      setAddPart(null); setPartsQuery(''); setPartsResults([]);
      enqueueSnackbar('Part added to ticket', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || 'Could not add part', { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const changeQty = async (li: any, delta: number) => {
    const q = Math.max(0.5, (li.quantity || 1) + delta);
    try {
      const r = await lineItemsApi.update(ticketId, li.id, { quantity: q });
      setLineItems((prev) => prev.map((x) => (x.id === li.id ? r.data : x)));
    } catch {}
  };

  const removeLine = async (li: any) => {
    if (!window.confirm(`Remove "${li.description}"?`)) return;
    try {
      await lineItemsApi.delete(ticketId, li.id);
      setLineItems((prev) => prev.filter((x) => x.id !== li.id));
    } catch {}
  };

  const lineTotal = lineItems.reduce((s, li) => s + (li.line_total || 0), 0);

  // ── visit outcome ─────────────────────────────────────────────────────
  const submitOutcome = async () => {
    if (!outcome) return;
    setBusy('outcome');
    try {
      if (outcome === 'complete') {
        setCloseRefused(null);
        await ticketsApi.changeStatus(ticketId, 'completed', outcomeNotes || undefined);
        enqueueSnackbar('Call completed — billing will pick it up', { variant: 'success' });
      } else {
        await ticketsApi.visitOutcome(ticketId, outcome, outcomeNotes || undefined);
        enqueueSnackbar(
          outcome === 'return_trip' ? 'Return trip flagged — dispatch will re-schedule' : 'Sent back to dispatch',
          { variant: 'success' },
        );
      }
      setOutcome(null); setOutcomeNotes('');
      navigate('/tech');
    } catch (e: any) {
      // change 16 (2026-11-01, P2-E): show the API's own message (e.g. "A dispatcher has to approve closing
      // and billing this ticket first") instead of a generic error, and keep it on screen.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : 'Could not record outcome');
      if (outcome === 'complete' && e?.response?.status === 403) {
        setCloseRefused(msg);
        setOutcome(null);
        enqueueSnackbar('Ask the dispatcher to approve the close', { variant: 'warning' });
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } finally {
      setBusy(null);
    }
  };

  // ── render ────────────────────────────────────────────────────────────
  if (loading) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !ticket) return <Alert severity="error">{error || 'Ticket not found'}</Alert>;

  const closed = ['completed', 'cancelled', 'denied'].includes(ticket.status);
  const currentIdx = STEP_ORDER.indexOf(ticket.status);
  const bigBtn = { minHeight: 52, borderRadius: 2.5, fontWeight: 700, textTransform: 'none' as const, fontSize: '1rem' };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {ticket.ticket_number}
            </Typography>
            <StatusChip status={ticket.status} />
            <ReturnTripChip ticket={ticket} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mb: 0.5 }}>
            {ticket.title}
          </Typography>
          {ticket.customer_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{ticket.customer_name}</Typography>
          )}
          <Typography variant="body2" sx={{ color: '#14548C', fontWeight: 600, mb: 1 }}>
            {fmtSched(ticket.scheduled_start)}
            {ticket.scheduled_end ? ` – ${new Date(ticket.scheduled_end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
          </Typography>
          {ticket.description && (
            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>{ticket.description}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              component="a" href={mapsHref(ticket)} target="_blank" rel="noopener"
              variant="outlined" startIcon={<DirectionsIcon />} sx={{ ...bigBtn, flex: 1, minWidth: 140 }}
            >
              Directions
            </Button>
            {ticket.contact_phone && (
              <Button
                component="a" href={`tel:${ticket.contact_phone}`}
                variant="outlined" startIcon={<PhoneIcon />} sx={{ ...bigBtn, flex: 1, minWidth: 140 }}
              >
                {ticket.contact_name ? `Call ${ticket.contact_name.split(' ')[0]}` : 'Call contact'}
              </Button>
            )}
          </Box>
          {ticketAddress(ticket) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {ticketAddress(ticket)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Status progression */}
      {!closed && (
        <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <CardContent>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Job status</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {STEPS.map((s) => {
                const stepIdx = STEP_ORDER.indexOf(s.status);
                const isCurrent = ticket.status === s.status;
                const isPast = currentIdx >= 0 && stepIdx <= currentIdx && !isCurrent;
                return (
                  <Button
                    key={s.status}
                    onClick={() => setStatus(s.status)}
                    disabled={isCurrent || !!busy}
                    startIcon={s.icon}
                    variant={isCurrent ? 'contained' : isPast ? 'text' : 'outlined'}
                    sx={{
                      ...bigBtn, flex: 1, px: 0.5,
                      ...(isCurrent ? { background: CONCEPT_GRADIENT, '&.Mui-disabled': { background: CONCEPT_GRADIENT, color: '#fff', opacity: 0.9 } } : {}),
                    }}
                  >
                    {busy === s.status ? <CircularProgress size={20} /> : s.label}
                  </Button>
                );
              })}
            </Box>
            {ticket.status === 'pending_parts' && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                Waiting on parts — tap “Working” to resume once the part arrives.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Time tracking */}
      <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon sx={{ color: '#14548C' }} />
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Time</Typography>
          </Box>

          {openEntry ? (
            <Box sx={{ mt: 1, p: 2, borderRadius: 2.5, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#4f46e5' }}>
                {openEntry.entry_type === 'travel' ? 'TRAVEL' : 'ON-SITE'} TIMER RUNNING
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', my: 0.5 }}>
                {`${Math.floor(liveMinutes / 60)}`.padStart(2, '0')}:{`${Math.floor(liveMinutes % 60)}`.padStart(2, '0')}:{`${Math.floor((liveMinutes * 60) % 60)}`.padStart(2, '0')}
              </Typography>
              <Button
                onClick={() => stopTimer(openEntry)} disabled={!!busy}
                variant="contained" color="error" startIcon={<StopIcon />} sx={{ ...bigBtn, width: '100%' }}
              >
                Stop timer
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button onClick={() => startTimer('onsite')} disabled={!!busy || closed} variant="contained"
                startIcon={<StartIcon />} sx={{ ...bigBtn, flex: 1, background: CONCEPT_GRADIENT }}>
                Start on-site
              </Button>
              <Button onClick={() => startTimer('travel')} disabled={!!busy || closed} variant="outlined"
                startIcon={<EnRouteIcon />} sx={{ ...bigBtn, flex: 1 }}>
                Start travel
              </Button>
            </Box>
          )}

          {timeSummary && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Chip size="small" label={`On-site ${fmtMinutes(timeSummary.total_onsite_minutes || 0)}`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
              <Chip size="small" label={`Travel ${fmtMinutes(timeSummary.total_travel_minutes || 0)}`} sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700 }} />
              <Chip size="small" label={`Visits ${timeSummary.visit_count || 0}`} sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 700 }} />
            </Box>
          )}

          {(timeSummary?.entries || []).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {(timeSummary.entries as any[]).map((e) => (
                <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: '1px solid #f1f5f9' }}>
                  <Chip label={e.entry_type} size="small" sx={{
                    height: 20, fontSize: 10, fontWeight: 700,
                    bgcolor: e.entry_type === 'onsite' ? '#e8f5e9' : e.entry_type === 'travel' ? '#e3f2fd' : '#fff3e0',
                    color: e.entry_type === 'onsite' ? '#2e7d32' : e.entry_type === 'travel' ? '#1565c0' : '#e65100',
                  }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {e.started_at ? new Date(e.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                      {' → '}
                      {e.ended_at ? new Date(e.ended_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'live'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtMinutes(e.duration_minutes || 0)}
                      {e.adjusted && <Chip label="ADJUSTED" size="small" sx={{ ml: 0.5, height: 16, fontSize: 8, fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309' }} />}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => adjustEntry(e)} aria-label="Adjust"><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                  <IconButton size="small" color="error" onClick={() => deleteEntry(e)} aria-label="Delete"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Accordion disableGutters elevation={0} sx={{ mt: 1, '&:before': { display: 'none' }, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={600}>Add manual entry</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={manualEntry.entry_type} label="Type"
                  onChange={(e) => setManualEntry({ ...manualEntry, entry_type: e.target.value })}>
                  <MenuItem value="onsite">On-Site</MenuItem>
                  <MenuItem value="travel">Travel / Drive Time</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" fullWidth label="Start" type="datetime-local" value={manualEntry.started_at}
                onChange={(e) => setManualEntry({ ...manualEntry, started_at: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField size="small" fullWidth label="End" type="datetime-local" value={manualEntry.ended_at}
                onChange={(e) => setManualEntry({ ...manualEntry, ended_at: e.target.value })} InputLabelProps={{ shrink: true }} />
              <TextField size="small" fullWidth label="Notes" value={manualEntry.notes}
                onChange={(e) => setManualEntry({ ...manualEntry, notes: e.target.value })} />
              <Button variant="contained" onClick={addManualEntry}
                disabled={busy === 'manual' || !manualEntry.started_at || !manualEntry.ended_at}
                sx={{ ...bigBtn, background: CONCEPT_GRADIENT }}>
                {busy === 'manual' ? 'Adding…' : 'Add entry'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Manual entries are logged with your name and auto-flagged ADJUSTED for billing review.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Parts */}
      <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PartsIcon sx={{ color: '#14548C' }} />
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', flex: 1 }}>Parts used</Typography>
            <Button size="small" startIcon={<TruckIcon />} onClick={() => { setTransferPart(null); setTransferOpen(true); }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Request to truck
            </Button>
          </Box>

          <TextField
            fullWidth size="medium" placeholder="Search parts catalog…" value={partsQuery}
            onChange={(e) => searchParts(e.target.value)}
          />
          {partsResults.length > 0 && (
            <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1, mt: 1 }}>
              {partsResults.map((p: any) => (
                <Box key={p.id} onClick={() => pickPart(p)}
                  sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', '&:active': { bgcolor: '#f1f5f9' } }}>
                  <Box sx={{ minWidth: 0, pr: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>{p.part_number}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{p.name}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="primary" sx={{ whiteSpace: 'nowrap' }}>
                    ${Number(p.unit_price || 0).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {lineItems.length > 0 ? (
            <Box sx={{ mt: 1.5 }}>
              {lineItems.map((li: any) => (
                <Box key={li.id} sx={{ py: 1, borderTop: '1px solid #f1f5f9' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{li.description}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {li.part_number || 'manual'} · {(li.item_condition || 'new')}
                        {(li.discount_percent || 0) > 0 ? ` · ${li.discount_percent}% off` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                      ${Number(li.line_total || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <IconButton size="small" onClick={() => changeQty(li, -1)} disabled={(li.quantity || 1) <= 1} aria-label="Less"><RemoveIcon fontSize="small" /></IconButton>
                    <Typography variant="body2" sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700 }}>{li.quantity}</Typography>
                    <IconButton size="small" onClick={() => changeQty(li, 1)} aria-label="More"><AddIcon fontSize="small" /></IconButton>
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" color="error" onClick={() => removeLine(li)} aria-label="Remove"><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #e2e8f0' }}>
                <Typography variant="body2" fontWeight={700}>Parts subtotal</Typography>
                <Typography variant="body2" fontWeight={700} color="primary">${lineTotal.toFixed(2)}</Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No parts on this ticket yet — search the catalog above.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Pictures: camera -> ticket Files tab (Tech submitted) */}
      <TechPicturesCard ticketId={ticketId} />

      {/* Notes */}
      <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Notes</Typography>
          <TextField label="Internal notes (office only)" multiline minRows={2} fullWidth value={notes.internal_notes}
            onChange={(e) => { setNotes({ ...notes, internal_notes: e.target.value }); setNotesDirty(true); }} />
          <TextField label="Customer notes (visible to customer)" multiline minRows={2} fullWidth value={notes.customer_notes}
            onChange={(e) => { setNotes({ ...notes, customer_notes: e.target.value }); setNotesDirty(true); }} />
          <TextField label="Resolution notes" multiline minRows={2} fullWidth value={notes.resolution_notes}
            onChange={(e) => { setNotes({ ...notes, resolution_notes: e.target.value }); setNotesDirty(true); }} />
          <Button variant="contained" onClick={saveNotes} disabled={!notesDirty || busy === 'notes'}
            sx={{ ...bigBtn, background: CONCEPT_GRADIENT }}>
            {busy === 'notes' ? 'Saving…' : 'Save notes'}
          </Button>
        </CardContent>
      </Card>

      {/* Visit outcome */}
      {!closed && (
        <Card sx={{ mb: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <OutcomeIcon sx={{ color: '#14548C' }} />
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Wrap up this visit</Typography>
            </Box>
            {closeRefused && (
              <Alert severity="warning" onClose={() => setCloseRefused(null)}>
                <b>Ask the dispatcher to approve the close.</b> {closeRefused}
              </Alert>
            )}
            {!closeRefused && !(ticket as any)?.close_approved_by && (
              <Typography variant="caption" color="text.secondary">
                Completing this call needs the dispatcher's "Approve close" first. If you are done, ask dispatch to approve it.
              </Typography>
            )}
            <Button variant="contained" color="success" startIcon={<CompleteIcon />} sx={bigBtn}
              onClick={() => { setOutcome('complete'); setOutcomeNotes(''); }}>
              Complete call
            </Button>
            <Button variant="outlined" color="warning" startIcon={<ReturnIcon />} sx={bigBtn}
              onClick={() => { setOutcome('return_trip'); setOutcomeNotes(''); }}>
              Return trip needed
            </Button>
            <Button variant="outlined" startIcon={<BackToDispatchIcon />} sx={bigBtn}
              onClick={() => { setOutcome('return_to_dispatch'); setOutcomeNotes(''); }}>
              Back to dispatch
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add-part dialog */}
      <Dialog open={!!addPart} onClose={() => setAddPart(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Add part</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          {addPart && (
            <>
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Typography variant="body2" fontWeight={700}>{addPart.part_number}</Typography>
                <Typography variant="caption" color="text.secondary">{addPart.name}</Typography>
                <Typography variant="body2" fontWeight={700} color="primary" sx={{ mt: 0.5 }}>
                  ${Number(addPart.unit_price || 0).toFixed(2)}/{addPart.unit_of_measure || 'ea'}
                </Typography>
                {myTruck && (
                  <Chip
                    size="small" icon={<TruckIcon />} sx={{ mt: 0.75, fontWeight: 700 }}
                    color={truckQty !== null && truckQty > 0 ? 'success' : 'default'}
                    label={`Your truck: ${truckQty === null ? '…' : truckQty}`}
                  />
                )}
                {truckQty !== null && truckQty <= 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Not on your truck — you can request a transfer after adding it.
                  </Typography>
                )}
              </Box>
              <TextField type="number" label="Quantity" value={addForm.quantity}
                inputProps={{ min: 0.5, step: 0.5, inputMode: 'decimal' }}
                onChange={(e) => setAddForm({ ...addForm, quantity: parseFloat(e.target.value) || 1 })} />
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select value={addForm.item_condition} label="Condition"
                  onChange={(e) => setAddForm({ ...addForm, item_condition: e.target.value })}>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="refurbished">Refurbished</MenuItem>
                  <MenuItem value="used">Used</MenuItem>
                </Select>
              </FormControl>
              <TextField type="number" label="Discount % (optional)" value={addForm.discount_percent}
                inputProps={{ min: 0, max: 100, inputMode: 'numeric' }}
                onChange={(e) => setAddForm({ ...addForm, discount_percent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setAddPart(null)} sx={{ minHeight: 44 }}>Cancel</Button>
          {addPart && truckQty !== null && truckQty <= 0 && (
            <Button startIcon={<TruckIcon />} sx={{ minHeight: 44, textTransform: 'none' }}
              onClick={() => { setTransferPart(addPart); setTransferOpen(true); }}>
              Request transfer
            </Button>
          )}
          <Button variant="contained" onClick={submitAddPart} disabled={busy === 'addpart' || !addPart}
            sx={{ minHeight: 44, background: CONCEPT_GRADIENT }}>
            {busy === 'addpart' ? 'Adding…' : 'Add to ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outcome confirm dialog */}
      <Dialog open={!!outcome} onClose={() => setOutcome(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {outcome === 'complete' ? 'Complete this call?'
            : outcome === 'return_trip' ? 'Flag return trip'
              : 'Send back to dispatch'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          <Alert severity={outcome === 'complete' ? 'success' : outcome === 'return_trip' ? 'warning' : 'info'} sx={{ py: 0 }}>
            {outcome === 'complete'
              ? 'Closes the ticket, finalizes your time, and sends it to billing (ERP invoice + truck stock issue).'
              : outcome === 'return_trip'
                ? 'Keeps the ticket open and tells dispatch to schedule a return visit. Say what still needs to be done.'
                : 'Unassigns the ticket and returns it to the dispatch queue.'}
          </Alert>
          <TextField
            autoFocus={outcome !== 'complete'}
            label={outcome === 'complete' ? 'Closing notes (optional)' : 'Reason / what remains'}
            multiline minRows={2} fullWidth value={outcomeNotes}
            onChange={(e) => setOutcomeNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOutcome(null)} disabled={busy === 'outcome'} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button
            variant="contained"
            color={outcome === 'complete' ? 'success' : outcome === 'return_trip' ? 'warning' : 'primary'}
            onClick={submitOutcome}
            disabled={busy === 'outcome' || (outcome !== 'complete' && !outcomeNotes.trim())}
            sx={{ minHeight: 44 }}
          >
            {busy === 'outcome' ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <TransferRequestDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        initialPart={transferPart}
      />
    </Box>
  );
}
